import { useCallback, useState } from "react";
import { core } from "../../lib/api";

interface TranslateResponse {
  translations: string[];
  target: string;
  warning?: string;
}

/**
 * Batch-translate transcript lines to English via Core.
 * POST /api/v1/analytics/translate (LLM + Mongo cache on the backend).
 * Keeps index alignment with the input so callers can map back 1:1.
 */
export function useTranscriptTranslation() {
  const [translated, setTranslated] = useState<string[] | null>(null);
  const [translating, setTranslating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const translate = useCallback(
    async (
      texts: string[],
      opts?: { callId?: string | null; contactId?: string | null },
    ) => {
      const cleaned = texts.map((t) => t ?? "");
      // Skip the round-trip when there is nothing to translate.
      if (cleaned.every((t) => !t.trim())) return cleaned;
      setTranslating(true);
      setError(null);
      try {
        const res = await core<TranslateResponse>("/api/v1/analytics/translate", {
          method: "POST",
          body: JSON.stringify({
            texts: cleaned,
            call_id: opts?.callId ?? null,
            contact_id: opts?.contactId ?? null,
            source: "auto",
          }),
        });
      const list = Array.isArray(res.translations) ? res.translations : cleaned;
      // Preserve alignment even if the backend truncates.
      const aligned = cleaned.map((original, i) => list[i] ?? original);
      setTranslated(aligned);
      return aligned;
    } catch (e: any) {
      setError(e?.message || "Translation failed. Showing original.");
      return cleaned;
    } finally {
      setTranslating(false);
    }
  }, []);

  const reset = useCallback(() => {
    setTranslated(null);
    setError(null);
  }, []);

  return { translated, translating, error, translate, reset, setTranslated };
}
